import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable, signal } from "@angular/core";
import { Observable } from "rxjs";
import { shareReplay } from "rxjs/operators";
import { environment } from "#environments/environment";
import { LoggedUser, User } from "models";
import { IUser } from "helpers/interfaces/user/user.interface";
import { IUserRole } from "helpers/interfaces/user/user-role.interface";
import { IDowntime } from "#models/event/downtime.model";

@Injectable()
export class UserService {
  private user = signal<LoggedUser | null>(JSON.parse(sessionStorage.getItem("currentUser")));
  private _usersAndRolesCache$: Observable<User[]> | null = null;

  constructor(private http: HttpClient) {}

  get(userID?: number, siteID?: number): Observable<User[] | User> {
    const params = new HttpParams().set("siteID", siteID ? siteID : "");

    const user = userID ? "/" + userID : "";
    return this.http.get<User[] | User>(
      `${environment.apiEndpoint}/users` + user,
      { params },
    );
  }

  getUsersandRoles(forceRefresh = false): Observable<User[]> {
    if (forceRefresh || !this._usersAndRolesCache$) {
      this._usersAndRolesCache$ = this.http
        .get<User[]>(`${environment.apiEndpoint}/users/usersandroles`, {
          headers: {
            "Cache-Control": "no-cache",
          },
        })
        .pipe(shareReplay(1));
    }

    return this._usersAndRolesCache$;
  }

  put(
    userID: number,
    businessUnitID: number,
    siteId?: number,
  ): Observable<User> {
    const params = { userID, businessUnitID, siteId };
    return this.http.put<User>(
      `${environment.apiEndpoint}/users`,
      {},
      { params },
    );
  }

  getByKeywordk(keyword: string, siteID?: number): Observable<User[] | User> {
    const params = {
      ...(keyword && { Keyword: keyword }),
      ...(siteID && { SiteID: siteID }),
    };
    return this.http.get<User[] | User>(`${environment.apiEndpoint}/users`, {
      params,
    });
  }

  getRoles(userID: number): Observable<IUserRole[]> {
    return this.http.get<IUserRole[]>(
      `${environment.apiEndpoint}/users/${userID}/roles`,
    );
  }

  postSiteUser(siteID: number, payload: IUser): Observable<any> {
    return this.http.post<any>(
      `${environment.apiEndpoint}/sites/${siteID}/users`,
      {
        ...payload,
        UserID: payload?.ID,
      },
    );
  }

  putSiteUser(siteID: number, payload: IUser): Observable<any> {
    return this.http.put<any>(
      `${environment.apiEndpoint}/sites/${siteID}/users/${payload.ID}`,
      {
        ...payload,
        UserID: payload?.ID,
      },
    );
  }

  deleteSiteUser(siteID: number, payload: IUser): Observable<any> {
    return this.http.delete<any>(
      `${environment.apiEndpoint}/sites/${siteID}/users/${payload.ID}`,
    );
  }

  deleteUser(userID: number): Observable<any> {
    return this.http.post<any>(
      `${environment.apiEndpoint}/users/${userID}/disable`,
      {},
    );
  }

  enableUser(userID: number): Observable<any> {
    return this.http.post<any>(
      `${environment.apiEndpoint}/users/${userID}/enable`,
      {},
    );
  }

  grantSysAdmin(
    userID: number,
    businessUnitID: number,
    siteId?: number,
  ): Observable<any> {
    return this.http.post<any>(
      `${environment.apiEndpoint}/users/${userID}/grant-sysadmin-role`,
      {},
    );
  }

  revokeSysAdmin(
    userID: number,
    businessUnitID: number,
    siteId?: number,
  ): Observable<any> {
    return this.http.post<any>(
      `${environment.apiEndpoint}/users/${userID}/revoke-sysadmin-role`,
      {},
    );
  }

  getCurrentUser(): Observable<LoggedUser> {
    return this.http.get<LoggedUser>(
      `${environment.apiEndpoint}/users/current`,
    );
  }

  getUsers(query: string, siteID?: number): Observable<User[]> {
    const params = new HttpParams()
      .set("query", query)
      .set("siteID", siteID ? siteID : "");
    return this.http.get<User[]>(
      `${environment.apiEndpoint}/users/participants`,
      { params },
    );
  }

  getBySite(siteId: number): Observable<User[]> {
    return this.http.get<User[]>(
      `${environment.apiEndpoint}/sites/${siteId}/users`,
    );
  }

  getUsersByOrg(orgId: number): Observable<User[]> {
    return this.http.get<User[]>(
      `${environment.apiEndpoint}/organizations/${orgId}/users`,
    );
  }

  post(user: User, siteID?: number): Observable<User> {
    const params = new HttpParams().set("siteID", siteID ? siteID : "");
    return this.http.post<User>(
      `${environment.apiEndpoint}/users`,
      {
        ...user,
        ID: undefined,
        siteID: siteID,
      },
      { params },
    );
  }

  postBySite(user: User, siteId?: number): Observable<User> {
    return this.http.post<User>(
      `${environment.apiEndpoint}/sites/${siteId}/users`,
      {
        ...user,
        ID: undefined,
      },
    );
  }

  putBySite(user: User, siteId: number): Observable<User> {
    return this.http.post<User>(
      `${environment.apiEndpoint}/sites/${siteId}/users`,
      {
        ...user,
      },
    );
  }

  acceptDisclaimer(userId: number): Observable<User> {
    return this.http.post<User>(
      `${environment.apiEndpoint}/users/current/accept-disclaimer`,
      null,
    );
  }

  listUserGuides(): Observable<Array<string>> {
    return this.http.get<Array<string>>(
      `${environment.apiEndpoint}/users/guides`,
      {
        headers: {
          "Cache-Control": "max-age=3600",
        },
      },
    );
  }

  postDowntimeUser(userId: number, downtime: IDowntime): Observable<IDowntime> {
    return this.http.post<IDowntime>(
      `${environment.apiEndpoint}/users/${userId}/downtimes`,
      { ...downtime },
    );
  }

  deleteDowntimeUser(
    userId: number,
    downtimeId: string,
  ): Observable<IDowntime> {
    return this.http.delete<IDowntime>(
      `${environment.apiEndpoint}/users/${userId}/downtimes/${downtimeId}`,
    );
  }
  
  getUser(): LoggedUser {
    return this.user();
  }
}
